import { createContext, useContext, useState, useEffect } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Available themes
export const THEMES = {
  'blue-purple': {
    name: 'Blue Purple',
    description: 'Deep blue from logo with purple accents',
    primary: '#1E3A5F',
    secondary: '#7C3AED',
    accent: '#9B59B6',
    preview: 'linear-gradient(135deg, #1E3A5F 0%, #7C3AED 100%)'
  },
  'forest-green': {
    name: 'Forest Green',
    description: 'Natural earthy green tones',
    primary: '#1B4332',
    secondary: '#E07A5F',
    accent: '#F2CC8F',
    preview: 'linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)'
  },
  'ocean-blue': {
    name: 'Ocean Blue',
    description: 'Calm ocean-inspired blues',
    primary: '#0369A1',
    secondary: '#0891B2',
    accent: '#22D3EE',
    preview: 'linear-gradient(135deg, #0369A1 0%, #0891B2 100%)'
  },
  'sunset-orange': {
    name: 'Sunset Orange',
    description: 'Warm sunset gradient colors',
    primary: '#EA580C',
    secondary: '#DC2626',
    accent: '#FBBF24',
    preview: 'linear-gradient(135deg, #EA580C 0%, #DC2626 100%)'
  }
};

const ThemeContext = createContext();

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState('blue-purple');
  const [loading, setLoading] = useState(true);

  // Fetch theme from backend on mount
  useEffect(() => {
    fetchTheme();
  }, []);

  // Apply theme class to document
  useEffect(() => {
    // Remove all theme classes
    document.documentElement.classList.remove(
      'theme-blue-purple',
      'theme-forest-green',
      'theme-ocean-blue',
      'theme-sunset-orange'
    );
    // Add current theme class
    document.documentElement.classList.add(`theme-${theme}`);
  }, [theme]);

  const fetchTheme = async () => {
    try {
      const response = await fetch(`${API}/api/settings/theme`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        if (data.theme && THEMES[data.theme]) {
          setTheme(data.theme);
        }
      }
    } catch (error) {
      console.error('Error fetching theme:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateTheme = async (newTheme, authHeaders = {}) => {
    if (!THEMES[newTheme]) return false;
    
    try {
      const response = await fetch(`${API}/api/admin/settings/theme`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...authHeaders
        },
        credentials: 'include',
        body: JSON.stringify({ theme: newTheme })
      });
      
      if (response.ok) {
        setTheme(newTheme);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error updating theme:', error);
      return false;
    }
  };

  return (
    <ThemeContext.Provider value={{ theme, setTheme: updateTheme, themes: THEMES, loading }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
